import { z } from "zod";
import type { ShareExpirationPolicy } from "../share/expiration.js";
import type { ShareLink } from "../share/share-link.js";
import { shareLink } from "../share/share-link.js";
import type { ShareTarget } from "../share/target.js";
import { CodecError, instantSchema } from "./envelope.js";

/**
 * Phase 10a codec for `ShareLink`. Determinism: declared key order;
 * undefined `revokedAt` omitted (never emitted as `null`); per-arm
 * payload schemas re-validate on read so a tampered wire shape can't
 * land an invalid link.
 *
 * Decode reconstructs the value through the `shareLink` smart factory
 * — every R-SHARE-1 invariant (non-empty id/token/ownerId, non-empty
 * target id, expiration validity, revoked-after-created ordering)
 * re-fires on read just as it does on direct construction.
 */

const shareTargetJsonSchema: z.ZodType<ShareTarget> = z.discriminatedUnion(
  "kind",
  [
    z.object({ kind: z.literal("site"), siteId: z.string().min(1) }),
    z.object({ kind: z.literal("panel"), panelId: z.string().min(1) }),
  ],
);

export function encodeShareTarget(t: ShareTarget): Record<string, unknown> {
  switch (t.kind) {
    case "site":
      return { kind: "site", siteId: t.siteId };
    case "panel":
      return { kind: "panel", panelId: t.panelId };
  }
}

const shareExpirationJsonSchema: z.ZodType<ShareExpirationPolicy> =
  z.discriminatedUnion("kind", [
    z.object({
      kind: z.literal("duration"),
      seconds: z.number().int().positive(),
    }),
    z.object({ kind: z.literal("never") }),
  ]);

export function encodeShareExpiration(
  p: ShareExpirationPolicy,
): Record<string, unknown> {
  switch (p.kind) {
    case "duration":
      return { kind: "duration", seconds: p.seconds };
    case "never":
      return { kind: "never" };
  }
}

export const shareLinkJsonSchema = z.object({
  id: z.string().min(1),
  token: z.string().min(1),
  target: shareTargetJsonSchema,
  ownerId: z.string().min(1),
  expirationPolicy: shareExpirationJsonSchema,
  createdAt: instantSchema,
  revokedAt: instantSchema.optional(),
});

export type ShareLinkJson = z.infer<typeof shareLinkJsonSchema>;

export function encodeShareLink(link: ShareLink): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: link.id,
    token: link.token,
    target: encodeShareTarget(link.target),
    ownerId: link.ownerId,
    expirationPolicy: encodeShareExpiration(link.expirationPolicy),
    createdAt: link.createdAt,
  };
  if (link.revokedAt !== undefined) {
    out["revokedAt"] = link.revokedAt;
  }
  return out;
}

export function decodeShareLink(input: unknown): ShareLink {
  const parsed = shareLinkJsonSchema.safeParse(input);
  if (!parsed.success) {
    throw new CodecError(
      "INVALID_PAYLOAD",
      `decodeShareLink: ${parsed.error.message}`,
    );
  }
  const j = parsed.data;
  return shareLink({
    id: j.id,
    token: j.token,
    target: j.target,
    ownerId: j.ownerId,
    expirationPolicy: j.expirationPolicy,
    createdAt: j.createdAt,
    ...(j.revokedAt !== undefined ? { revokedAt: j.revokedAt } : {}),
  });
}
